/**
 * Punto de entrada de la consola (`/panel`).
 *
 * Comparte login, tokens y cliente de API con el ERP —es la misma aplicación—
 * pero no comparte pantalla: no monta el `AppShell`, así que abrirla en otra
 * pestaña del navegador da exactamente lo que se pidió, una consola aparte.
 *
 * El control de acceso de acá es de interfaz. La seguridad la sigue haciendo el
 * backend endpoint por endpoint; lo que evita esta guarda es mostrarle a alguien
 * un tablero entero de botones que le van a contestar 403.
 */

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { ErrorBoundary } from '@/components/shell/ErrorBoundary';
import { Button } from '@/components/shell/primitives';
import { esAdministrador, nombreVisible } from '@/panel/acceso';
import { EstadoScreen } from '@/panel/EstadoScreen';
import { PanelLayout } from '@/panel/PanelLayout';
import { PermisosScreen } from '@/panel/PermisosScreen';
import { UsuariosScreen } from '@/panel/UsuariosScreen';
import { Panel } from '@/panel/ui';
import { selectActiveCompany, useSessionStore } from '@/store/session';

/**
 * Pantalla de acceso denegado.
 *
 * Sin jerga: no dice «403», no dice «falta el grupo base.administrador». Dice
 * qué es esto, por qué no se abre y a quién pedírselo. Quien llega acá casi
 * siempre siguió un enlace que le pasaron.
 */
function SinAcceso() {
  const user = useSessionStore((estado) => estado.user);
  const signOut = useSessionStore((estado) => estado.signOut);

  return (
    <div className="grid min-h-svh place-items-center bg-app px-4 py-10">
      <div className="w-full max-w-lg rounded-xl border border-line bg-surface p-6 shadow-sm sm:p-8">
        <p className="text-2xs font-semibold uppercase tracking-[0.14em] text-fg-faint">Núcleo · Consola</p>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">Esta pantalla no es para tu usuario</h1>
        <p className="mt-3 text-fg-muted">
          El panel de control sirve para configurar el sistema: quién entra, qué puede hacer cada persona y
          qué hay instalado. Está reservado a quien administra Núcleo.
        </p>
        <p className="mt-3 text-fg-muted">
          Entraste como <strong className="text-fg">{nombreVisible(user)}</strong>. Si necesitás acceso,
          pedíselo a quien administra el sistema en tu organización; si te equivocaste de cuenta, cerrá la
          sesión y volvé a entrar.
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          <a
            href="/"
            className="inline-flex h-10 items-center rounded-md bg-accent px-4 text-sm font-medium text-accent-contrast shadow-sm transition-colors hover:bg-accent-hover"
          >
            Ir a mi trabajo
          </a>
          <Button variant="secondary" onClick={signOut}>
            Cerrar sesión
          </Button>
        </div>
      </div>
    </div>
  );
}

function NoEncontrado() {
  return (
    <Panel title="Esa sección no existe">
      <p className="text-sm text-fg-muted">
        La dirección no corresponde a ninguna sección de la consola. Usá las pestañas de arriba.
      </p>
    </Panel>
  );
}

export function PanelApp() {
  const user = useSessionStore((estado) => estado.user);
  const tenant = useSessionStore((estado) => estado.tenant);
  const companies = useSessionStore((estado) => estado.companies);
  const modules = useSessionStore((estado) => estado.modules);
  const activeCompany = useSessionStore(selectActiveCompany);
  const { pathname } = useLocation();

  if (!esAdministrador(user)) return <SinAcceso />;

  return (
    <PanelLayout user={user} tenant={tenant} company={activeCompany}>
      {/* La frontera envuelve el contenido y no el armazón: si una sección se
          cae, las pestañas siguen y se puede ir a otra sin recargar. */}
      <ErrorBoundary resetKey={pathname}>
        <Routes>
          <Route
            path="/panel"
            element={
              <EstadoScreen
                modulos={modules}
                companies={companies}
                tenant={tenant}
                activeCompany={activeCompany}
              />
            }
          />
          <Route path="/panel/permisos" element={<PermisosScreen />} />
          <Route path="/panel/usuarios" element={<UsuariosScreen />} />
          {/* `/panel/algo-viejo` no tira al 404 del ERP: se queda en la consola. */}
          <Route path="/panel/inicio" element={<Navigate to="/panel" replace />} />
          <Route path="*" element={<NoEncontrado />} />
        </Routes>
      </ErrorBoundary>
    </PanelLayout>
  );
}
